import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

/** Modal for creating or editing a cluster. */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    ds.input.text.Component,
    ds.input.textArea.Component,
    ds.input.select.single.Component,
  ],
  selector: 'atn-space-cluster-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.space.full.clusters.Collection} */
  public readonly clustersEc = ngCore.inject(
    state.space.full.clusters.Collection,
  )

  /** Keep selected organization values by hierarchy entity id. */
  public readonly selectionLookupSg = ngCore.signal<
    Partial<ents.extra.space.Organization.Lookup>
  >({})

  /** Modal title based on whether creating or editing */
  public readonly titleSg = ngCore.computed(
    (
      isCreating = this.clustersEc.plugins.currentEntityPlugin.feature.selectors
        .isCreating()
        .asSignal(),
    ) => (isCreating ? 'Create Cluster' : 'Edit Cluster'),
  )

  /** Whether a leaf organization has been selected. */
  public readonly leafOrganizationSelectedSg = ngCore.computed(
    (
      selectionLookup = this.selectionLookupSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => {
      const leafEntity = schema.tryGetLeafEntityOf(
        state.space.organizations.constants.DIMENSION_ID,
      )
      return (
        TypeChecker.isNotNullish(leafEntity) &&
        TypeChecker.isNotNullish(selectionLookup[leafEntity.id])
      )
    },
  )

  /** Organization options by hierarchy entity based on parent selection. */
  public readonly organizationEntityOptionsSg = ngCore.computed(
    (
      selectionLookup = this.selectionLookupSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) =>
      schema
        .getEntitiesInDimension(
          state.space.organizations.constants.DIMENSION_ID,
        )
        .map(entity => {
          const parentEntity = schema.tryGetParentEntityOf(entity)
          const parentId = parentEntity
            ? selectionLookup[parentEntity.id]
            : undefined
          const orgEc = this._organizationsFeature.selectors
            .getOrganizationEc(entity.id)
            .asSignal()

          return {
            id: entity.id,
            name: entity.name,
            options:
              orgEc?.plugins.cachePlugin.feature.selectors
                .getCachedEntities()
                .asSignal()
                .filter(TypeChecker.isNotNullish.raw)
                .filter(o => o.parentId === parentId)
                .map(({ name: label, id: value }) => ({ label, value })) ?? [],
          }
        }),
  )

  /** Whether user can submit the cluster. */
  public readonly canSaveSg = ngCore.computed(
    (
      cluster = this.clustersEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal(),
      isCreating = this.clustersEc.plugins.currentEntityPlugin.feature.selectors
        .isCreating()
        .asSignal(),
      hasLeafOrganization = this.leafOrganizationSelectedSg(),
    ) => {
      const hasName = !!cluster?.name && cluster.name.trim().length > 0
      return isCreating ? hasName && hasLeafOrganization : hasName
    },
  )

  /** Handles selection of an organization record at a given entity. */
  public handleOrganizationSelection(
    entityId: ents.core.dms.models.Entity.Id,
    organizationId?: ents.extra.space.Organization.Id,
  ): void {
    const schema = this._schemaFeature.selectors.schema.asSignal()

    if (schema.isLeaf(entityId) && TypeChecker.isNotNullish(organizationId)) {
      this.updateCluster({ organizationId })
    }

    const ancestorIds = new Set(schema.getAncestorsOf(entityId).map(e => e.id))

    this.selectionLookupSg.update(prev => {
      const pruned: Partial<ents.extra.space.Organization.Lookup> = {}
      for (const ancestorId of ancestorIds) {
        const value = prev[ancestorId]
        if (TypeChecker.isNotNullish(value)) {
          pruned[ancestorId] = value
        }
      }
      if (TypeChecker.isNotNullish(organizationId)) {
        pruned[entityId] = organizationId
      }
      return pruned
    })
  }

  /**
   * Update cluster
   */
  public updateCluster(
    input: Partial<
      ents.extra.space.Cluster.Input & ents.extra.space.Cluster.Update
    >,
  ): void {
    this.clustersEc.plugins.editingPlugin.feature.actions.edit
      .from('Space.Clusters.Modal.Changed')
      .dispatch({ edit: { input } })
  }

  /** Cancel dialog. */
  public cancelClickHandler(): void {
    this.clustersEc.plugins.editingPlugin.feature.actions.discard
      .from('Space.Clusters.Modal.Cancel.Clicked')
      .dispatch({ current: true, skipUnset: true })
  }

  /** Save cluster. */
  public saveClickHandler(): void {
    this.clustersEc.plugins.editingPlugin.feature.actions.submit
      .from('Space.Clusters.Modal.Save.Clicked')
      .dispatch({ current: true, skipUnset: true })
  }
}
